import {useConfig} from '@aboutyou/config';
import {
  OrderDetailsSection,
  OrderDetailsSection_Address,
  OrderDetailsSection_AddressTab,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/order_success_page/v1/sections.types';
import {useTranslation} from '@aboutyou/i18n';
import {useTrackFeatureInViewImpression} from '@aboutyou/tracking';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  Tabs,
  Text,
  desktopAndUpLinaria,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {css} from '@linaria/core';
import {styled} from '@linaria/react';
import React, {startTransition, useMemo, useState} from 'react';
import {BuyNowPayLaterSection} from './BuyNowPayLaterSection';

interface OrderInfoItemProps {
  label: string;
  value: string;
  'data-testid'?: string;
}

const OrderInfoItem = ({label, value, ...props}: OrderInfoItemProps) => (
  <OrderInfoItemWrapper {...props}>
    <OrderInfoItemLabel>{label}</OrderInfoItemLabel>
    <Text>{value}</Text>
  </OrderInfoItemWrapper>
);

const ConcatinatedAddress = ({
  address,
  ...props
}: {
  address: OrderDetailsSection_Address;
  'data-testid'?: string;
}) => {
  const stringPairs = useMemo(
    () => [
      `${address.firstName} ${address.lastName}`,
      `${address.street} ${address.houseNumber}`,
      `${address.zipCode} ${address.city}`,
      address.country,
    ],
    [address],
  );

  return (
    <ConcatinatedAddressWrapper data-testid={props['data-testid']}>
      <Text size="px14" weight="mediumBold">
        {stringPairs.join(' • ')}
      </Text>
    </ConcatinatedAddressWrapper>
  );
};

const AddressTabs = ({
  addresses,
}: {
  addresses: OrderDetailsSection_AddressTab[];
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const selectedAddress = addresses[selectedTab];

  const handleTabChange = (value: number) => {
    startTransition(() => {
      setSelectedTab(value);
    });
  };

  const tabs = useMemo(
    () =>
      addresses.map((addr, i) => ({
        label: addr.label,
        value: i,
      })),
    [addresses],
  );

  if (!selectedAddress) return null;

  if (addresses.length === 1) {
    return (
      <SingleAddressWrapper>
        <Text size="px18" weight="mediumHeavy">
          {selectedAddress.label}
        </Text>
        <ConcatinatedAddress
          data-testid="addressDetails"
          address={selectedAddress.address}
        />
      </SingleAddressWrapper>
    );
  }

  return (
    <div>
      <StyledTabs
        onTabClick={handleTabChange}
        selectedTabValue={selectedTab}
        tabs={tabs}
        inactiveTextColor="Grey1"
      />
      <TabBody>
        <ConcatinatedAddress
          data-testid="addressDetails"
          address={selectedAddress.address}
        />
      </TabBody>
    </div>
  );
};

const Addresses = ({
  addresses,
}: {
  addresses: OrderDetailsSection_AddressTab[];
}) => {
  const {device} = useConfig();

  return device === 'desktop' ? (
    <AddressPlain addresses={addresses} />
  ) : (
    <AddressWrapper>
      <AddressTabs addresses={addresses} />
    </AddressWrapper>
  );
};

const AddressPlain = (props: {addresses: OrderDetailsSection_AddressTab[]}) => {
  return (
    <>
      {props.addresses.map((address, index) => (
        <HeadlineSeparatedContent
          key={index}
          headline={address.label}
          data-testid={'ospAddress' + index}
        >
          <Text as="p">
            {address.address.firstName} {address.address.lastName}
          </Text>
          <Text as="p">
            {address.address.street} {address.address.houseNumber}
          </Text>
          <Text as="p">
            {address.address.zipCode} {address.address.city}
          </Text>
          {address.address.country && (
            <Text as="p">{address.address.country}</Text>
          )}
        </HeadlineSeparatedContent>
      ))}
    </>
  );
};

const HeadlineSeparatedContent = (
  props: React.PropsWithChildren<{
    headline: string;
    'data-testid'?: string;
  }>,
) => {
  return (
    <HeadlineSeparatedContentWrapper data-testid={props['data-testid']}>
      <Headline weight="mediumHeavy">{props.headline}</Headline>
      <HeadlineSperatedContentChildren>
        {props.children}
      </HeadlineSperatedContentChildren>
    </HeadlineSeparatedContentWrapper>
  );
};

export const OrderConfirmation = (props: OrderDetailsSection) => {
  const {
    headline,
    date,
    orderId,
    customerId,
    paymentMethod,
    addresses,
    buyNowPayLaterSection,
  } = props;

  const {t} = useTranslation();

  const {ref} = useTrackFeatureInViewImpression<HTMLDivElement>({
    label: 'order_details',
    name: 'order_details',
  });

  return (
    <OrderInfoWrapper
      ref={ref}
      className={buyNowPayLaterSection ? wrappedModeStyles : flexModeStyles}
    >
      <HeadlineSeparatedContent headline={headline}>
        <OrderInfoItem
          data-testid="ospOrderDate"
          label={t('Order date:')}
          value={date}
        />
        <OrderInfoItem
          data-testid="ospOrderNumber"
          label={t('Order number:')}
          value={orderId}
        />
        <OrderInfoItem
          data-testid="ospOrderCustomerId"
          label={t('Customer number:')}
          value={customerId.toString()}
        />
        {paymentMethod && !buyNowPayLaterSection && (
          <OrderInfoItem
            data-testid="ospOrderPaymentType"
            label={t('Payment method:')}
            value={paymentMethod}
          />
        )}
      </HeadlineSeparatedContent>
      {buyNowPayLaterSection && (
        <BuyNowPayLaterSection {...buyNowPayLaterSection} />
      )}
      <Addresses addresses={addresses} />
    </OrderInfoWrapper>
  );
};

const SingleAddressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem 0;
`;

const StyledTabs = styled(Tabs)`
  border-bottom: 1px solid ${COLORS.Grey3};
  font-weight: ${FONT_WEIGHTS.mediumBold};
`;

const ConcatinatedAddressWrapper = styled.div`
  display: flex;
  line-height: 1.42;
`;

const TabBody = styled.div`
  padding: 1rem 1rem 1.5rem;
`;

const AddressWrapper = styled.div`
  border-top: 1px solid ${COLORS.Grey3};
  border-bottom: 1px solid ${COLORS.Grey3};
`;

const wrappedModeStyles = css`
  ${tabletAndUpLinaria(`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
    padding: unset;

    > *:last-child:nth-child(odd) {
      grid-column: 1 / -1;
    }
  `)}

  ${desktopAndUpLinaria(`
    display: flex;
    justify-content: space-between;

    > *:last-child:nth-child(odd) {
      grid-column: unset;
    }
  `)}
`;

const flexModeStyles = css`
  ${tabletAndUpLinaria(`
    display: flex;
    gap: 1.25rem;
    justify-content: space-between;
    padding: unset;
  `)}
`;

const OrderInfoWrapper = styled.div`
  padding: 0 1rem;
`;

const HeadlineSeparatedContentWrapper = styled.div`
  padding: 1.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  line-height: 1.42;

  ${tabletAndUpLinaria(`
    padding: 1.5rem;
    border: 1px solid ${COLORS.Grey3};
    border-radius: 0.25rem;
  `)}
`;
const HeadlineSperatedContentChildren = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Headline = styled(Text)`
  font-size: ${FONT_SIZES.px18};
  font-style: normal;
  line-height: 1.44;

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px16};
    line-height: 1.5;
  `)}
`;

const OrderInfoItemLabel = styled(Text)`
  width: 55%;

  ${tabletAndUpLinaria(`
    width: auto;
    margin-right: 0.25rem;
  `)}
`;

const OrderInfoItemWrapper = styled.div`
  display: flex;
  line-height: 1.42;
  font-weight: ${FONT_WEIGHTS.mediumBold};
  gap: 0.25rem;

  ${tabletAndUpLinaria(`
    flex-wrap: wrap;
    gap: 0;
  `)}
`;
